import {queryOptions, useMutation} from '@tanstack/react-query';
import {type} from 'arktype';
import React, {ComponentType} from 'react';
import {VerificationMethodType} from '~/features/two-factor';
import {
    expectEither,
    HttpClient,
    POST,
    ServerValidationResult,
    useHttpClient,
    usePublicHttpClient,
} from '~/library/network-facade';

const VerificationMethodSuccess = type({
    code: '200',
    message: 'string',
    methods: VerificationMethodType.array(),
});

const VerificationMethodFailure = ServerValidationResult.to(
    type({
        username: 'string',
    })
);

export function fetchVerificationMethods(http: HttpClient, username: string) {
    return http(
        'v1/verification_methods',
        POST({
            username,
        })
    ).then(
        ...expectEither({
            200: VerificationMethodSuccess,
            400: VerificationMethodFailure,
        })
    );
}

export function fetchSessionVerificationMethods(http: HttpClient) {
    return http('v1/otp/verification_methods', POST()).then(
        ...expectEither({
            200: VerificationMethodSuccess,
        })
    );
}

export function useVerificationMethodsMutation() {
    const http = usePublicHttpClient();

    return useMutation({
        mutationKey: ['v1/verification_methods'],
        async mutationFn(username: string) {
            const res = await fetchVerificationMethods(http, username);

            switch (res.status) {
                case 200: {
                    return {value: res.json.methods};
                }
                case 400: {
                    return {problem: res.json};
                }
                case 'Abort': {
                    throw res.reason;
                }
            }
        },
    });
}

export function useVerificationMethodsQuery() {
    const http = useHttpClient();
    const placeholderData: VerificationMethodType[] = [];
    const {queryKey, queryFn} = queryOptions({
        queryKey: ['v1/otp/verification_methods'],
        queryFn: async ({client, queryKey}) => {
            const res = await fetchSessionVerificationMethods(http);

            switch (res.status) {
                case 200: {
                    return res.json.methods;
                }
                case 'Abort': {
                    await client.cancelQueries({queryKey});
                    throw res.reason;
                }
            }
        },
        placeholderData: placeholderData,
    });

    return {
        queryKey,
        queryFn: queryFn as typeof queryFn, // weird hack to stop ts from downcasting queryFn to symbol | queryFn
        placeholderData,
    };
}

export type VerificationMethodsMutation = ReturnType<typeof useVerificationMethodsMutation>;

type WithVerificationMethods = {
    verificationMethodsMutation?: VerificationMethodsMutation;
};

export function withVerificationMethodsMutation<P extends WithVerificationMethods>(Component: ComponentType<P>) {
    return function WithVerificationMethods(props: Omit<P, 'verificationMethodsMutation'>) {
        const mutation = useVerificationMethodsMutation();
        return React.createElement(Component, {...props, verificationMethodsMutation: mutation} as P);
    };
}
